/**
 * Vehicle Lookup Hook
 *
 * Provides a mutation for looking up vehicle data from external registry services.
 */

import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatFinnishPlate } from '@/utils/finnishPlate';

export interface VehicleLookupResult {
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  bodyType: string | null;
  color: string | null;
  doors: number | null;
  seats: number | null;
  fuelType: string | null;
  engineDisplacementCc: number | null;
  enginePowerKw: number | null;
  transmission: string | null;
  driveType: string | null;
  curbWeightKg: number | null;
  grossWeightKg: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  vehicleClass: string | null;
  vehicleGroup: string | null;
  co2EmissionsGkm: number | null;
  euroEmissionClass: string | null;
  firstRegistrationDate: string | null;
  rawData: Record<string, unknown>;
  fetchedAt: string;
  provider: string;
}

interface LookupParams {
  registrationNumber: string;
  force?: boolean;
  vin?: string;
}

export function useVehicleLookup() {
  return useMutation({
    mutationFn: ({ registrationNumber, force, vin }: LookupParams) => {
      // Paid registry lookup only for a complete Finnish plate (#4122).
      // Callers in the vehicle form and the detail card hit this guard too,
      // including an explicit refresh. An incomplete plate never reaches the API.
      const plate = formatFinnishPlate(registrationNumber);
      if (plate === null) {
        return Promise.reject(new Error('Registration lookup requires a complete Finnish plate'));
      }
      const params = new URLSearchParams();
      if (force) params.set('force', 'true');
      const knownVin = typeof vin === 'string' ? vin.trim() : '';
      if (knownVin) params.set('vin', knownVin);
      const qs = params.toString();
      return api.get<VehicleLookupResult>(
        `/vehicles/lookup/${encodeURIComponent(plate)}${qs ? `?${qs}` : ''}`,
      );
    },
  });
}
